import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, input, OnInit, Output } from '@angular/core';
import { ParamMap, RouterModule } from '@angular/router';

import { PSYCHIATRIST_EDUCATION_LEVELS } from '@sondermind/constants';
import { ProviderPillsComponent } from '@sondermind/features/provider-pills';
import { IRequestSessionParams } from '@sondermind/features/public-facing/session-request';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IProviderShareableProfile } from '@sondermind/utilities/models-public-facing';
import { StringArrayToCommaListPipe } from '@sondermind/utilities/pipes';
import { StringArrayToCommaListNoConjunctionPipe }
  from '@sondermind/utilities/pipes/string-array-to-comma-list-no-conjunction.pipe';
import { BreakpointsService } from '@sondermind/utilities/services';
import { USStateNameMap } from '@sondermind/utilities/states-list';

import { IDirectoryAppConfig } from '@sondermind/configuration/directory-configuration.interface';
import { ConfigurationService } from '@sondermind/configuration';
import { EmptyURIValue } from '@sondermind/data-access/directory-link';
import { WindowRefService } from '@sondermind/utilities/window';
import { SessionType } from '@sondermind/utilities/models-session';
import { ITopSpecialty } from '../../models/top-specialty.interface';
import { getValidStateNames } from '../../utils/valid-states.util';

export enum ProviderTypes {
  PSYCH = 'Psychiatry Provider',
  NON_PSYCH = 'Therapist'
}

@Component({
  selector: 'landing-page-header',
  imports: [
    CommonModule,
    IrisIconComponent,
    RouterModule,
    ProviderPillsComponent,
    StringArrayToCommaListPipe,
    StringArrayToCommaListNoConjunctionPipe
  ],
  templateUrl: './landing-page-header.component.html',
  styleUrls: ['./landing-page-header.component.scss']
})
export class LandingPageHeaderComponent implements OnInit {
  readonly providerShareableProfile = input.required<IProviderShareableProfile>();
  readonly paramsShareCode = input<string>('');
  readonly paramsName = input<string>('');
  readonly queryParamMap = input.required<ParamMap>();
  readonly requestSessionQueryParams = input<IRequestSessionParams | null>(null);
  readonly topSpecialties = input<ITopSpecialty[]>([]);
  @Output() requestASessionClicked: EventEmitter<void> = new EventEmitter<void>();

  validStates: string[] = [];
  maxLengthOfInsuranceListString = 56;
  noProfilePhoto: boolean = true;

  queryParamSpecialties: string[] = [];
  providerType: ProviderTypes = ProviderTypes.NON_PSYCH;
  topSpecialtiesIds: number[] = [];
  certificationAbbreviations: string[] = [];
  readonly providerSessionTypesOffered = computed(() => this.providerShareableProfile().session_types_offered);
  readonly offersInPerson = computed(() => this.providerSessionTypesOffered().includes(SessionType.IN_PERSON));
  readonly offersVideo = computed(() => this.providerSessionTypesOffered().includes(SessionType.VIDEO));

  providerNetworkCompanyIds: number[] = [];
  providerAcceptedInsuranceNames: string[] = [];

  iconColor: IconColors = IconColors.BRAND1;
  iconSizes = IconSizes;

  constructor(
    public breakpointService: BreakpointsService,
    private configurationService: ConfigurationService<IDirectoryAppConfig>,
    private windowRef: WindowRefService
  ){}

  ngOnInit(): void {
    this.noProfilePhoto = this.providerShareableProfile().profile_photo_url.endsWith('/missing.png');

    this.validStates = getValidStateNames(this.providerShareableProfile());
    this.queryParamSpecialties = this.queryParamMap().get('specialties')?.split(',') ?? [];
    this.topSpecialtiesIds = this.providerShareableProfile().top_specialties.map((specialty) => specialty.id);

    this.providerType = this.checkProviderType();
    this.certificationAbbreviations = this.createCertificationAbbreviationList();
    this.handleProviderInsurance();
  }

  private checkProviderType(): ProviderTypes {
    const isPsychiatrist = PSYCHIATRIST_EDUCATION_LEVELS.includes(this.providerShareableProfile().education_level);
    return isPsychiatrist ? ProviderTypes.PSYCH : ProviderTypes.NON_PSYCH;
  }

  // Since providers can have the same certification in multiple states, we don't show duplicates
  // Uniqueness is handled by the StringArrayToCommaListNoConjunction pipe
  private createCertificationAbbreviationList(): string[] {
    return this.providerShareableProfile().certifications.map((cert) => cert.abbrev);
  }

  private handleProviderInsurance(): void {
    // Get unique insurance names because provider_network_companies will contain many pncs with the same insurance name
    this.providerAcceptedInsuranceNames = [...new Set(
      this.providerShareableProfile().provider_network_companies.map((pnc) => pnc.insurance_name)
    )];

    this.providerNetworkCompanyIds = this.providerShareableProfile().provider_network_companies.map((pnc) => pnc.id);
  }

  scrollToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  onRequestASessionClick(): void {
    this.requestASessionClicked.emit();
  }

  navigateToProviderDirectory(stateName: string): void {
    // Look up state abbreviation from full state name
    const stateMapping = USStateNameMap.find((state) => state.name === stateName);

    // Get base URL from config with fallback
    const providerDirectoryRoot = this.configurationService.env.providerDirectoryBase
      ?? 'https://www.sondermind.com/provider-directory';

    // Construct URL with state filter and empty city placeholder
    const stateAbbrev = stateMapping?.value.toLowerCase() ?? '';
    const providerDirectoryUrl = `${providerDirectoryRoot}/sta-${stateAbbrev}/cty-${EmptyURIValue}`;

    // Redirect to provider directory
    if (this.windowRef.nativeWindow) {
      this.windowRef.nativeWindow.location.href = providerDirectoryUrl;
    }
  }
}
